import type { ApiResponse } from '@/types';

function getApiBaseUrl(): string {
  const envUrl = import.meta.env.VITE_API_URL;
  if (!envUrl) return '/api';
  if (envUrl.endsWith('/api')) return envUrl;
  return envUrl.replace(/\/$/, '') + '/api';
}

const API_BASE_URL = getApiBaseUrl();

async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = localStorage.getItem('auth_token');

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  if (window.Telegram?.WebApp?.initData) {
    (headers as Record<string, string>)['X-Telegram-Init-Data'] = window.Telegram.WebApp.initData;
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  const data: ApiResponse<T> = await response.json();

  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Unknown error');
  }

  return data.data as T;
}

// ============================================
// TYPES
// ============================================

export interface StageInfo {
  stage: number;
  stageName: string;
  activeDays: number;
  totalDays: number;
  currentBlock: number;
  blockProgress: {
    block: number;
    activeDays: number;
    transfersCount: number;
    realPracticesCount: number;
    canComplete: boolean;
  };
  paceMode: string;
  paceLabel: string;
  startedAt: string | null;
  hasTodayPractice: boolean;
  alreadyLoggedToday: boolean;
}

export interface Checkpoint {
  stage: number;
  block: number;
  activeDays: number;
  transfersCount: number;
  realPracticesCount: number;
  status: string;
  startedAt: string;
  completedAt: string | null;
}

export interface LogActiveDayResult {
  activeDays: number;
  message: string;
}

export interface CompleteBlockResult {
  nextBlock: number | null;
  stageCompleted: boolean;
  message: string;
}

// ============================================
// API
// ============================================

export async function getCurrentStage(): Promise<StageInfo> {
  return request<StageInfo>('/stages/current');
}

export async function logActiveDay(): Promise<LogActiveDayResult> {
  return request<LogActiveDayResult>('/stages/log-active-day', { method: 'POST' });
}

export async function getCheckpoints(): Promise<Checkpoint[]> {
  return request<Checkpoint[]>('/stages/checkpoints');
}

export async function completeBlock(): Promise<CompleteBlockResult> {
  return request<CompleteBlockResult>('/stages/complete-block', { method: 'POST' });
}
